import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  title: string;
  onClose?: () => void;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}

export function Modal({ title, onClose, children, size = 'md' }: Props) {
  const navigate = useNavigate();
  const close = onClose ?? (() => navigate(-1));

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [close]);

  // xl ≈ lg + 20% (56rem → 67rem) — для заявочных окон, где переносы
  // сумм/курсов на вторую строку ломают читаемость.
  const widthCls = size === 'xl' ? 'max-w-[67rem]' : size === 'lg' ? 'max-w-4xl' : 'max-w-xl';
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-2 sm:p-4"
      style={{ background: 'var(--bg-overlay)' }}
    >
      <div className={`my-2 w-full sm:my-8 ${widthCls} overflow-hidden rounded-lg border border-border bg-bg shadow-lg`}>
        <div className="flex items-center justify-between gap-4 border-b border-border-subtle bg-bg2 px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="min-w-0 break-words text-base font-semibold tracking-tight text-text sm:text-lg">{title}</h2>
          <button
            type="button"
            onClick={close}
            aria-label="Закрыть"
            className="-mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-xl leading-none text-text2 transition-colors duration-150 hover:bg-bg3 hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            ×
          </button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}
